// app/menuFragments.jsx — extracted from App.js (behaviour-preserving split).
import { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, Plus, Sparkles } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent } from "@/components/ui/card";
import { Drawer, DrawerContent, DrawerDescription, DrawerFooter, DrawerHeader, DrawerTitle } from "@/components/ui/drawer";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/components/ui/sonner";
import { computeSidesPricingForLine } from "@/app/fragments";
import { formatPrice } from "@/app/core";


// ---- SpecialsCarouselBanner ----------------------------------------------
// Auto-advancing banner that surfaces active specials to the customer.
// • 1 special  → static card (no nav UI).
// • 2+ specials → gentle crossfade carousel with dot indicators + tap-to-
//   advance, a visible slide counter, and prev/next arrows for accessibility.
// Pauses on pointer hover/touch and respects `prefers-reduced-motion`.
function SpecialsCarouselBanner({ specials }) {
  const list = Array.isArray(specials) ? specials.filter(Boolean) : [];
  const count = list.length;
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const reducedMotion = useMemo(() => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    try { return window.matchMedia("(prefers-reduced-motion: reduce)").matches; }
    catch (_e) { return false; }
  }, []);

  // Clamp the index if the special list shrinks (e.g. a promo ends mid-visit
  // and the 60-second menu refresh removes it from the payload).
  useEffect(() => {
    if (count === 0) return;
    setIndex((i) => (i >= count ? 0 : i));
  }, [count]);

  // Auto-advance every 5s when there's more than one special. Pause on
  // hover / touch so the user can read the current promo, and skip entirely
  // when the user has prefers-reduced-motion set.
  useEffect(() => {
    if (count <= 1 || paused || reducedMotion) return;
    const t = setInterval(() => {
      setIndex((i) => (i + 1) % count);
    }, 5000);
    return () => clearInterval(t);
  }, [count, paused, reducedMotion]);

  if (count === 0) return null;
  const current = list[Math.min(index, count - 1)] || list[0];

  const goto = (next) => {
    if (!count) return;
    const n = ((next % count) + count) % count;
    setIndex(n);
  };

  return (
    <Card
      className="relative overflow-hidden rounded-[24px] border-[hsl(var(--accent))]/40 bg-gradient-to-br from-[hsl(var(--accent))]/12 via-[hsl(var(--primary))]/6 to-transparent"
      data-testid="customer-specials-banner"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={() => setPaused(true)}
      onTouchEnd={() => setPaused(false)}
      aria-roledescription={count > 1 ? "carousel" : undefined}
      aria-label={count > 1 ? `${count} specials running` : undefined}
    >
      <CardContent className="flex items-start gap-3 p-4">
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-[hsl(var(--accent))]/15 text-[hsl(var(--accent))]">
          <Sparkles className="h-5 w-5" />
        </div>
        {/* Slides stack: only the active one is visible; older/newer fade
            out in-place so height stays stable across transitions. */}
        <div
          className="relative min-w-0 flex-1"
          aria-live="polite"
          aria-atomic="true"
        >
          {list.map((sp, i) => {
            const isActive = i === index;
            return (
              <div
                key={sp.id || i}
                className={`${i === 0 ? "" : "absolute inset-0"} ${
                  isActive ? "opacity-100 translate-y-0" : "pointer-events-none opacity-0 translate-y-1"
                } transition-[opacity,transform] duration-300 ease-out`}
                aria-hidden={!isActive}
                data-testid={`specials-banner-slide-${i}`}
              >
                <div className="flex items-center gap-2">
                  <p className="text-xs font-medium uppercase tracking-[0.18em] text-[hsl(var(--accent))]">
                    Live now
                  </p>
                  {count > 1 ? (
                    <span className="text-[10px] font-medium uppercase tracking-[0.18em] text-muted-foreground" data-testid="specials-banner-counter">
                      {index + 1} / {count}
                    </span>
                  ) : null}
                </div>
                <p className="mt-0.5 font-['Space_Grotesk'] text-base font-semibold leading-tight">
                  {sp.name}
                </p>
                {sp.description ? (
                  <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">
                    {sp.description}
                  </p>
                ) : null}
              </div>
            );
          })}
        </div>
        {count > 1 ? (
          <div className="flex shrink-0 items-center gap-1">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-8 w-8 rounded-full text-muted-foreground hover:bg-[hsl(var(--accent))]/10 hover:text-[hsl(var(--accent))]"
              onClick={() => goto(index - 1)}
              aria-label="Previous special"
              data-testid="specials-banner-prev"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-8 w-8 rounded-full text-muted-foreground hover:bg-[hsl(var(--accent))]/10 hover:text-[hsl(var(--accent))]"
              onClick={() => goto(index + 1)}
              aria-label="Next special"
              data-testid="specials-banner-next"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        ) : null}
      </CardContent>
      {count > 1 ? (
        <div className="flex items-center justify-center gap-1.5 pb-3" role="tablist" aria-label="Specials">
          {list.map((sp, i) => {
            const active = i === index;
            return (
              <button
                key={sp.id || `dot-${i}`}
                type="button"
                role="tab"
                aria-selected={active}
                aria-label={`Show special ${i + 1}: ${sp.name || ""}`}
                onClick={() => goto(i)}
                className={`h-1.5 rounded-full transition-[width,background-color] duration-300 ${
                  active
                    ? "w-5 bg-[hsl(var(--accent))]"
                    : "w-1.5 bg-[hsl(var(--accent))]/30 hover:bg-[hsl(var(--accent))]/50"
                }`}
                data-testid={`specials-banner-dot-${i}`}
              />
            );
          })}
        </div>
      ) : null}
    </Card>
  );
}

// ---- ItemOptionsSheet -----------------------------------------------------
// Unified drawer that opens when a customer taps "Add" on a menu item that
// requires extra input (sides, meal prep prompts, or both). Renders each
// prep prompt as a RadioGroup above the sides picker, validates required
// answers, and computes a live extras total before sending the line to
// the cart.
export function SideSelectionSheet({ item, sidesLibrary, onClose, onConfirm, audience = "customer" }) {
  const open = Boolean(item);
  const cfg = item?.sides_config || { selection_mode: "none", free_count: 0, max_select: 1, allowed_side_ids: [], included_side_ids: [] };
  const prepPrompts = useMemo(() => Array.isArray(item?.prep_prompts) ? item.prep_prompts : [], [item?.prep_prompts]);
  const sidesEnabled = (cfg.selection_mode || "none") !== "none";
  // Per-item notes gate. notes_mode is one of:
  //  - "off"   (default — no notes shown anywhere)
  //  - "staff" (notes only show when audience === "staff")
  //  - "all"   (notes show for both customers and staff)
  const notesMode = String(item?.notes_mode || "off").toLowerCase();
  const notesAllowed = notesMode === "all" || (notesMode === "staff" && audience === "staff");
  const allowedSet = useMemo(() => new Set((cfg.allowed_side_ids || []).map(String)), [cfg.allowed_side_ids]);
  const allowedSides = useMemo(
    () => (sidesLibrary || []).filter((s) => s && allowedSet.has(String(s.id))),
    [sidesLibrary, allowedSet]
  );
  const [picked, setPicked] = useState(() => new Set((cfg.included_side_ids || []).map(String)));
  // Prep answers keyed by prompt id. Pre-fills the first option for
  // required prompts so a single-option list doesn't block the customer.
  const [prepAnswers, setPrepAnswers] = useState(() => ({}));
  // Optional free-form note that rides along with the line to the kitchen
  // or bar ticket (depending on the item's department).
  const [note, setNote] = useState("");

  // Reset picked + prep + note every time the dialog opens for a new item.
  useEffect(() => {
    if (item) {
      setPicked(new Set((item.sides_config?.included_side_ids || []).map(String)));
      setNote("");
      const seed = {};
      (Array.isArray(item.prep_prompts) ? item.prep_prompts : []).forEach((p) => {
        if (!p || !p.id) return;
        // Pre-select first option only when the prompt is required AND
        // has exactly one option (nothing meaningful for the customer to
        // choose). Otherwise leave blank so they make an explicit choice.
        if (p.required && Array.isArray(p.options) && p.options.length === 1) {
          seed[p.id] = p.options[0];
        } else {
          seed[p.id] = "";
        }
      });
      setPrepAnswers(seed);
    }
  }, [item]);

  const togglePick = (sideId) => {
    const id = String(sideId);
    setPicked((current) => {
      const next = new Set(current);
      if (next.has(id)) {
        next.delete(id);
      } else {
        // Enforce max_select; do nothing if at cap.
        if (next.size >= Math.max(1, Number(cfg.max_select) || 1)) {
          toast.error(`You can pick at most ${cfg.max_select} side(s) for this item.`);
          return current;
        }
        next.add(id);
      }
      return next;
    });
  };

  const pickedArr = useMemo(() => Array.from(picked), [picked]);
  const pricing = useMemo(() => computeSidesPricingForLine(item, pickedArr, sidesLibrary || []), [item, pickedArr, sidesLibrary]);
  const free = Math.max(0, Number(cfg.free_count) || 0);
  const remainingFree = Math.max(0, free - pickedArr.length);

  const handleConfirm = () => {
    // Validate required prep prompts first so the error points at the
    // missing answer rather than "pick a side".
    for (const prompt of prepPrompts) {
      if (!prompt || !prompt.id) continue;
      const value = prepAnswers[prompt.id];
      if (prompt.required && (!value || !String(value).trim())) {
        toast.error(`Please answer "${prompt.label}".`);
        return;
      }
    }
    if (sidesEnabled && cfg.selection_mode === "required" && pickedArr.length === 0) {
      toast.error("Please pick at least one side.");
      return;
    }
    const prepSelections = prepPrompts
      .filter((p) => p && p.id && prepAnswers[p.id])
      .map((p) => ({ label: p.label, choice: prepAnswers[p.id] }));
    onConfirm(pickedArr, prepSelections, (note || "").trim());
  };

  return (
    <Drawer open={open} onOpenChange={(v) => { if (!v) onClose(); }} shouldScaleBackground={false}>
      {/* Session 72 — phone-safe layout: the sheet is capped at the visible
          viewport (dvh — browser chrome included), the header and the
          Add-to-cart footer never move, and EVERYTHING in between (badges,
          notes, prep prompts, sides) scrolls as ONE region. Before, a fixed
          48vh inner scroller plus header/notes/footer added up to more than
          a phone screen and the confirm button sat below the fold. */}
      <DrawerContent className="flex max-h-[92dvh] flex-col rounded-t-[28px]" data-testid="side-selection-sheet">
        <div className="mx-auto flex min-h-0 w-full max-w-xl flex-1 flex-col">
          <DrawerHeader className="shrink-0 pb-2">
            <DrawerTitle className="font-['Space_Grotesk']">{item?.name}</DrawerTitle>
            <DrawerDescription>
              {prepPrompts.length ? "Tell the kitchen how you'd like it. " : ""}
              {sidesEnabled
                ? (cfg.selection_mode === "required" ? "Pick at least one side. " : "Pick the sides you'd like. ") +
                  (free === 0
                    ? "Each side is charged at the side price."
                    : free === 1
                    ? "First selected side is included. Extras are charged at the side price."
                    : `First ${free} selected sides are included. Extras are charged at the side price.`)
                : ""}
            </DrawerDescription>
          </DrawerHeader>
          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 pb-2" style={{ WebkitOverflowScrolling: "touch" }} data-testid="side-sheet-scroll">
            {sidesEnabled ? (
              <div className="mb-3 flex flex-wrap items-center gap-2">
                <Badge variant="secondary" className="border-border" data-testid="side-sheet-free-remaining">
                  {remainingFree > 0 ? `${remainingFree} free side${remainingFree === 1 ? "" : "s"} left` : "Free quota used"}
                </Badge>
                <Badge variant="outline" className="border-border" data-testid="side-sheet-extras-total">
                  Extras: {formatPrice(pricing.extras_per_unit)}
                </Badge>
                <Badge variant="outline" className="border-border">
                  Up to {Math.max(1, Number(cfg.max_select) || 1)} total
                </Badge>
              </div>
            ) : null}
            {notesAllowed ? (
              <div className="mb-3 space-y-1 rounded-2xl border border-border bg-secondary/40 p-3" data-testid="side-sheet-notes-block">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                    Notes <span className="font-normal normal-case text-muted-foreground/80">(optional)</span>
                  </p>
                  <span className="text-[10px] text-muted-foreground">{note.length}/200</span>
                </div>
                <Textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value.slice(0, 200))}
                  placeholder="e.g. no onions, extra crispy, allergic to nuts"
                  className="min-h-[72px] rounded-xl bg-background"
                  data-testid="side-sheet-note"
                />
              </div>
            ) : null}
            <div className="pr-1">
              <div className="space-y-4 pr-2 pb-2">
                {/* ---- Meal prep prompts ------------------------------- */}
                {prepPrompts.length ? (
                  <div className="space-y-4" data-testid="item-prep-prompts">
                    {prepPrompts.map((prompt) => {
                      if (!prompt || !prompt.id) return null;
                      const value = prepAnswers[prompt.id] || "";
                      return (
                        <div
                          key={prompt.id}
                          className="rounded-2xl border border-border bg-card p-3"
                          data-testid={`prep-prompt-${prompt.id}`}
                        >
                          <div className="mb-2 flex items-center gap-2">
                            <p className="text-sm font-semibold leading-tight">{prompt.label}</p>
                            {prompt.required ? (
                              <Badge variant="outline" className="border-border text-[10px] uppercase tracking-wide">Required</Badge>
                            ) : (
                              <Badge variant="secondary" className="border-border text-[10px] uppercase tracking-wide">Optional</Badge>
                            )}
                          </div>
                          <RadioGroup
                            value={value}
                            onValueChange={(v) => setPrepAnswers((current) => ({ ...current, [prompt.id]: v }))}
                            className="grid gap-2"
                            data-testid={`prep-prompt-radio-${prompt.id}`}
                          >
                            {(prompt.options || []).map((opt, idx) => {
                              const optId = `${prompt.id}-opt-${idx}`;
                              const selected = value === opt;
                              return (
                                <label
                                  key={optId}
                                  htmlFor={optId}
                                  className={`flex cursor-pointer items-center gap-3 rounded-xl border px-3 py-2 text-sm transition-colors ${
                                    selected
                                      ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/8"
                                      : "border-border bg-background hover:bg-secondary/50"
                                  }`}
                                  data-testid={`prep-option-${prompt.id}-${idx}`}
                                >
                                  <RadioGroupItem value={opt} id={optId} />
                                  <span className="min-w-0 truncate">{opt}</span>
                                </label>
                              );
                            })}
                          </RadioGroup>
                          {!prompt.required && value ? (
                            <button
                              type="button"
                              className="mt-2 text-xs font-medium text-muted-foreground underline underline-offset-4 hover:text-foreground"
                              onClick={() => setPrepAnswers((current) => ({ ...current, [prompt.id]: "" }))}
                              data-testid={`prep-clear-${prompt.id}`}
                            >
                              Clear answer
                            </button>
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                ) : null}

                {/* ---- Notes block moved above the scroll area so the
                       textarea never vanishes as the user scrolls through
                       the sides list ----------------------------------- */}

                {/* ---- Sides list -------------------------------------- */}
                {sidesEnabled ? (
                  allowedSides.length === 0 ? (
                    <div className="rounded-2xl border border-dashed border-border bg-secondary/40 p-4 text-sm text-muted-foreground">
                      No sides have been configured for this item yet.
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">Sides</p>
                      {allowedSides.map((side, idx) => {
                        const id = String(side.id);
                        const checked = picked.has(id);
                        const sideOrderIdx = pickedArr.indexOf(id);
                        const charged = checked && sideOrderIdx >= free;
                        return (
                          <button
                            key={id || idx}
                            type="button"
                            onClick={() => togglePick(id)}
                            className={`flex w-full items-start justify-between gap-3 rounded-2xl border px-4 py-3 text-left transition-colors ${
                              checked
                                ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/8"
                                : "border-border bg-card hover:bg-secondary/50"
                            }`}
                            data-testid={`side-choice-${id}`}
                            aria-pressed={checked}
                          >
                            <div className="flex min-w-0 items-start gap-3">
                              <Checkbox
                                checked={checked}
                                onCheckedChange={() => togglePick(id)}
                                className="mt-0.5"
                                aria-hidden
                                tabIndex={-1}
                              />
                              <div className="min-w-0">
                                <p className="font-medium leading-tight">{side.name}</p>
                                {side.description ? (
                                  <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">{side.description}</p>
                                ) : null}
                              </div>
                            </div>
                            <div className="text-right text-sm">
                              {checked ? (
                                charged ? (
                                  <>
                                    <p className="whitespace-nowrap font-semibold text-foreground">+{formatPrice(side.price)}</p>
                                    <p className="mt-0.5 text-xs uppercase tracking-wide text-muted-foreground">extra</p>
                                  </>
                                ) : (
                                  <p className="whitespace-nowrap text-sm font-semibold text-emerald-600 dark:text-emerald-400">Free</p>
                                )
                              ) : (
                                <p className="whitespace-nowrap text-xs text-muted-foreground">Tap to add</p>
                              )}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )
                ) : null}
              </div>
            </div>
          </div>
          <DrawerFooter className="shrink-0 gap-2 border-t border-border bg-background pb-[max(1rem,env(safe-area-inset-bottom))]">
            <Button
              type="button"
              className="h-11 rounded-xl bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90"
              onClick={handleConfirm}
              data-testid="side-sheet-confirm-button"
            >
              <Plus className="h-4 w-4" />
              Add to cart
              {pricing.extras_per_unit > 0 ? ` · ${formatPrice(pricing.extras_per_unit)} extras` : ""}
            </Button>
            <Button type="button" variant="outline" className="h-11 rounded-xl" onClick={onClose}>
              Cancel
            </Button>
          </DrawerFooter>
        </div>
      </DrawerContent>
    </Drawer>
  );
}
